import React, { useMemo, useState } from "react";

import clsx from "clsx";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DataTypeProvider,
  IntegratedSorting,
  type Sorting,
  SortingState,
} from "@devexpress/dx-react-grid";
import {
  Table as DxTable,
  Grid,
  TableFixedColumns,
  TableHeaderRow,
  VirtualTable,
} from "@devexpress/dx-react-grid-bootstrap4";

import { ColumnMenu } from "../../shared";

import { JourneyDataFormatter } from "./journey-data-formater";
import { PercentDataFormatter } from "./percent-data-formatter";
import { TouchpointsDataFormatter } from "./touchpoints-data-formatter";

interface Props {
  rows: any[];
}

export const Table = ({ rows }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const [sorting, setSorting] = useState<Sorting[]>([
    { columnName: "numPaths", direction: "desc" },
  ]);

  const rowsWithIds = useMemo(() => {
    const numPathsSum = rows.reduce((acc, e) => acc + e.numPaths, 0);
    const numConvertingPathsSum = rows.reduce(
      (acc, e) => acc + e.numConvertingPaths,
      0,
    );
    const revenueFromConvertingPathsSum = rows.reduce(
      (acc, e) => acc + e.revenueFromConvertingPaths,
      0,
    );

    return rows.map((e, i) => ({
      ...e,
      numPathsSum,
      numConvertingPathsSum,
      revenueFromConvertingPathsSum,
      conversionRate: e.numConvertingPaths / e.numPaths,
      id: i,
    }));
  }, [rows]);

  return (
    <Grid
      rootComponent={(props) => (
        <Grid.Root
          {...props}
          style={{ height: "100%" }}
          className="flyout-journeys-table-v3"
        />
      )}
      columns={[
        { name: "pathElements", title: "Touchpoints (unordered)" },
        {
          name: "touchPoints",
          title: "Touchpoints",
          getCellValue: ({ pathElements }) => pathElements.length,
        },
        { name: "numPaths", title: "Occurrences" },
        { name: "numConvertingPaths", title: "Conversions" },
        { name: "conversionRate", title: "Conv rate" },
        { name: "revenueFromConvertingPaths", title: "Revenue" },
      ]}
      rows={rowsWithIds}
      getRowId={(row) => row.id}
    >
      <SortingState sorting={sorting} onSortingChange={setSorting} />
      <IntegratedSorting />
      <VirtualTable
        height="auto"
        estimatedRowHeight={56}
        columnExtensions={[
          { columnName: "touchPoints", width: 150 },
          { columnName: "numPaths", width: 150 },
          { columnName: "numConvertingPaths", width: 150 },
          { columnName: "conversionRate", width: 140 },
          { columnName: "revenueFromConvertingPaths", width: 140 },
        ]}
        rowComponent={(props) => (
          <DxTable.Row {...props} className="odd:bg-white even:bg-[#fafafe]" />
        )}
      />
      <TableHeaderRow
        cellComponent={({ children }) => (
          <th
            className="position-relative dx-g-bs4-header-cell text-nowrap"
            scope="col"
            colSpan={1}
          >
            {children}
            <div className="header-column-separator"></div>
          </th>
        )}
        contentComponent={({ column }) => (
          <div
            className={clsx("flex items-center h-full text-left", {
              "justify-between": column.name === "pathElements",
              "justify-end": column.name !== "pathElements",
            })}
          >
            <div>{column.title}</div>
            <div className="flex items-center">
              {sorting[0].columnName === column.name && (
                <div
                  className={clsx(
                    "flex justify-center items-center text-cosmic bg-cosmic-3",
                    "rounded w-6 h-6 ml-2 hover:cursor-pointer",
                  )}
                  onClick={() => {
                    const direction =
                      sorting[0].direction === "asc" ? "desc" : "asc";
                    const newSorting: Sorting = {
                      columnName: column.name,
                      direction,
                    };

                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo: "Flyout Journey change sorting asc",
                      data: {
                        pageContext: { newSorting },
                      },
                    });
                    setSorting([newSorting]);
                  }}
                >
                  <i
                    className={clsx("fa-regular fa-sharp", {
                      "fa-arrow-up": sorting[0].direction === "asc",
                      "fa-arrow-down": sorting[0].direction === "desc",
                    })}
                  ></i>
                </div>
              )}
              {column.name !== "pathElements" && (
                <ColumnMenu
                  onSortAsc={() => {
                    const newSorting: Sorting = {
                      columnName: column.name,
                      direction: "asc",
                    };

                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo: "Flyout Journey change sorting asc",
                      data: {
                        pageContext: { newSorting },
                      },
                    });
                    setSorting([newSorting]);
                  }}
                  onSortDesc={() => {
                    const newSorting: Sorting = {
                      columnName: column.name,
                      direction: "desc",
                    };
                    prettyLogEventPage({
                      domain: "Flyout",
                      action: "onClick",
                      additionalActionInfo:
                        "Flyout Journey change sorting desc",
                      data: {
                        pageContext: { newSorting },
                      },
                    });
                    setSorting([newSorting]);
                  }}
                />
              )}
            </div>
          </div>
        )}
      />
      <DataTypeProvider
        for={["pathElements"]}
        formatterComponent={TouchpointsDataFormatter}
      />
      <DataTypeProvider
        for={["numPaths", "numConvertingPaths", "revenueFromConvertingPaths"]}
        formatterComponent={JourneyDataFormatter}
      />
      <DataTypeProvider
        for={["conversionRate"]}
        formatterComponent={PercentDataFormatter}
      />
      <TableFixedColumns leftColumns={["pathElements"]} />
    </Grid>
  );
};
